import React, { useState } from 'react';
import {
    Box,
    Button,
    FormHelperText,
    IconButton,
    InputAdornment,
    TextField,
    Typography,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import { format, isValid, parseISO } from 'date-fns';
import { getIn, useFormikContext } from 'formik';
import i18n from 'i18n-js';
import ObjectID from 'bson-objectid';
import { images } from 'assets';
import { DateTimePicker } from 'components/shared';
import { DATE_FORMAT } from 'helpers/reservationHelper';
import { ReservationBlockedDate } from 'store/restaurantDetailsStore/reducer';

const DATE_PICKER_TOGGLE_ID = 'date-time-picker';
// Fits dd.MM.yyyy plus the clear and calendar icons of the optional "to" field
const DATE_FIELD_WIDTH = 175;
type DateField = 'fromDate' | 'toDate';

export default function ReservationBlockedDatesSection() {
    const { values, errors, submitCount, isSubmitting, setValues } =
        useFormikContext<{ onlineRequestBlockedDates: ReservationBlockedDate[] }>();
    const [openPicker, setOpenPicker] = useState<{ index: number; field: DateField } | null>(null);
    const entries = values.onlineRequestBlockedDates;
    const text = (key: string) => i18n.t(`restaurant.reservationSettings.blockedDates.${key}`);

    function updateEntries(
        change: (entries: ReservationBlockedDate[]) => ReservationBlockedDate[]
    ) {
        setValues((current) => ({
            ...current,
            onlineRequestBlockedDates: change(current.onlineRequestBlockedDates),
        }));
    }

    function update(index: number, field: 'fromDate' | 'toDate' | 'reason', value: string | null) {
        updateEntries((entries) =>
            entries.map((entry, current) =>
                current === index ? { ...entry, [field]: value } : entry
            )
        );
    }

    function renderDateField(index: number, field: DateField, label: string, required = false) {
        const value = entries[index][field];
        const isPickerOpen = openPicker?.index === index && openPicker.field === field;
        const parsedDate = value ? parseISO(value) : null;
        const pickerDate = parsedDate && isValid(parsedDate) ? parsedDate : new Date();
        const inputId = isPickerOpen
            ? DATE_PICKER_TOGGLE_ID
            : `blocked-date-${entries[index].id}-${field}`;

        return (
            <Box key={field} position="relative">
                <TextField
                    id={inputId}
                    variant="filled"
                    style={{ width: DATE_FIELD_WIDTH }}
                    required={required}
                    label={label}
                    value={
                        parsedDate && isValid(parsedDate)
                            ? format(parsedDate, 'dd.MM.yyyy')
                            : value ?? ''
                    }
                    InputLabelProps={{ shrink: !!value || isPickerOpen }}
                    InputProps={{
                        style: { cursor: 'pointer' },
                        endAdornment: (
                            <InputAdornment position="end">
                                {field === 'toDate' && value && (
                                    <IconButton
                                        size="small"
                                        aria-label={i18n.t('reservations.filters.clearDate')}
                                        title={i18n.t('reservations.filters.clearDate')}
                                        onClick={(event) => {
                                            event.stopPropagation();
                                            setOpenPicker(null);
                                            update(index, field, null);
                                        }}
                                    >
                                        <CloseIcon fontSize="small" />
                                    </IconButton>
                                )}
                                <img src={images.icons.calendar} alt="" />
                            </InputAdornment>
                        ),
                    }}
                    inputProps={{
                        id: inputId,
                        readOnly: true,
                        onKeyDown: (event: React.KeyboardEvent<HTMLInputElement>) => {
                            if (event.key === 'Enter' || event.key === ' ') {
                                event.preventDefault();
                                setOpenPicker({ index, field });
                            }
                        },
                    }}
                    disabled={isSubmitting}
                    onClick={() => setOpenPicker({ index, field })}
                />
                {isPickerOpen && (
                    <DateTimePicker
                        showButtons
                        alignLeft
                        hideTimeSelect
                        date={pickerDate}
                        onSubmit={(date: Date) => update(index, field, format(date, DATE_FORMAT))}
                        isOpen
                        onClose={() => setOpenPicker(null)}
                    />
                )}
            </Box>
        );
    }

    return (
        <Box mt={3} id="onlineRequestBlockedDates" tabIndex={-1}>
            <Typography variant="caption" display="block" gutterBottom>
                {text('title').toUpperCase()}
            </Typography>
            <Typography variant="body2" color="textSecondary">
                {text('helper')}
            </Typography>
            {!entries.length && (
                <Box my={2}>
                    <Typography variant="body2">{text('empty')}</Typography>
                </Box>
            )}
            {entries.map((entry, index) => {
                const error =
                    submitCount > 0
                        ? getIn(errors, `onlineRequestBlockedDates.${index}.reason`)
                        : undefined;
                return (
                    <Box key={entry.id} mt={2}>
                        <Box display="flex" flexWrap="wrap" alignItems="center" style={{ gap: 12 }}>
                            {renderDateField(index, 'fromDate', text('from'), true)}
                            {renderDateField(index, 'toDate', text('to'))}
                            <TextField
                                id={`blocked-date-${entry.id}-reason`}
                                variant="filled"
                                required
                                label={text('reason')}
                                value={entry.reason}
                                style={{ flex: '1 1 260px' }}
                                inputProps={{ maxLength: 100 }}
                                disabled={isSubmitting}
                                onChange={(event) => update(index, 'reason', event.target.value)}
                            />
                            <Button
                                color="primary"
                                disabled={isSubmitting}
                                onClick={() =>
                                    updateEntries((entries) =>
                                        entries.filter((_, current) => current !== index)
                                    )
                                }
                            >
                                {'\u2013 '}
                                {text('remove')}
                            </Button>
                        </Box>
                        {error && (
                            <FormHelperText error role="alert" style={{ whiteSpace: 'pre-line' }}>
                                {error}
                            </FormHelperText>
                        )}
                    </Box>
                );
            })}
            <Box mt={1}>
                <Typography variant="caption" color="textSecondary">
                    {text('reasonHelper')}
                </Typography>
            </Box>
            <Button
                color="primary"
                disabled={isSubmitting}
                onClick={() =>
                    updateEntries((entries) => [
                        ...entries,
                        {
                            id: new ObjectID().toHexString(),
                            fromDate: '',
                            toDate: null,
                            reason: '',
                        },
                    ])
                }
            >
                + {text('add')}
            </Button>
        </Box>
    );
}
